import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Separator,
} from "@repo/ui";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  useAddTodo,
  useDeleteTodo,
  useRenameTodo,
  useTodos,
  useToggleTodo,
} from "@/lib/queries/use-todos";

export function Home() {
  const { data: todos, isLoading } = useTodos();
  const addTodo = useAddTodo();
  const toggleTodo = useToggleTodo();
  const renameTodo = useRenameTodo();
  const deleteTodo = useDeleteTodo();

  const [title, setTitle] = useState("");
  const [filter, setFilter] = useState<"all" | "active" | "completed">("all");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    if (!todos) return [];
    if (filter === "active") return todos.filter((t) => !t.completed);
    if (filter === "completed") return todos.filter((t) => t.completed);
    return todos;
  }, [todos, filter]);

  const handleAdd = async () => {
    const t = title.trim();
    if (!t) return;
    await addTodo.mutateAsync(t);
    setTitle("");
    inputRef.current?.focus();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/40 flex items-start justify-center px-4 py-16">
      <Card className="w-full max-w-xl border-border/60">
        <CardHeader className="space-y-2">
          <CardTitle className="text-3xl tracking-tight">Stardeck Todos</CardTitle>
          <p className="text-sm text-muted-foreground">Minimal, focused, fast.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              placeholder="Add a new task"
              className="h-12"
            />
            <Button onClick={handleAdd} className="h-12 px-4" disabled={addTodo.isPending}>
              <Plus className="size-5" />
            </Button>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <Button
              variant={filter === "all" ? "default" : "ghost"}
              onClick={() => setFilter("all")}
            >
              All
            </Button>
            <Button
              variant={filter === "active" ? "default" : "ghost"}
              onClick={() => setFilter("active")}
            >
              Active
            </Button>
            <Button
              variant={filter === "completed" ? "default" : "ghost"}
              onClick={() => setFilter("completed")}
            >
              Completed
            </Button>
            <Separator className="mx-2 h-6" orientation="vertical" />
            <span className="text-muted-foreground">{todos?.length ?? 0} total</span>
          </div>

          <div className="space-y-2">
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tasks yet.</p>
            ) : (
              filtered.map((t) => (
                <div
                  key={t.id}
                  className="group flex items-center gap-3 rounded-lg border px-3 py-2 bg-card hover:bg-accent/30"
                >
                  <Checkbox
                    checked={t.completed}
                    onCheckedChange={(v) => toggleTodo.mutate({ id: t.id, completed: Boolean(v) })}
                    aria-label={t.completed ? "Mark as active" : "Mark as completed"}
                    className="size-5"
                  />
                  <input
                    defaultValue={t.title}
                    onBlur={(e) => {
                      const v = e.currentTarget.value.trim();
                      if (v && v !== t.title) renameTodo.mutate({ id: t.id, title: v });
                      else e.currentTarget.value = t.title;
                    }}
                    className="flex-1 bg-transparent outline-none text-sm disabled:opacity-60 line-clamp-2"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteTodo.mutate(t.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
